// lib/exportResolve.ts — полный резолв id → имя для CSV-экспорта (FIX-C / U-15).
// Проблема: экспортная выборка (size=1000) ссылается на сущности, которых нет в page-scoped
// картах видимой страницы (uid-резолв падал в короткий UUID). Здесь: пользователи через
// lookupByIds (чанк ≤100 — backend cap), компании/контакты — постраничный сбор полного
// справочника (size ≤100, backend cap), сделки — поштучный GET /api/deals/{id}
// (batch-endpoint'а у сделок нет). Невозможное к резолву → '—' (ни одного UUID в CSV).

import { usersApi, buildUserMap } from '@/api/users';
import { companiesApi } from '@/api/companies';
import { contactsApi, contactDisplayName } from '@/api/contacts';
import { dealsApi } from '@/api/deals';
import type { Page } from '@/api/tasks';

/** Чанк ids для lookupByIds / поштучного резолва сделок (backend cap ≤100). */
const IDS_CHUNK_SIZE = 100;

/** Размер страницы справочников (backend валидирует size ≤ 100 → HTTP 500 на большее). */
const DIRECTORY_PAGE_SIZE = 100;

/** Чанк параллельных поштучных запросов сделок (KISS: не 1000 одновременных GET). */
const DEAL_FETCH_CHUNK_SIZE = 20;

/** Placeholder для отсутствующего значения (конвенция UI: «—»). */
const EMPTY = '—';

/** Режет массив на чанки заданного размера. */
function chunkStrings(items: string[], size: number): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

/**
 * Собирает map id → имя для сущностей справочника: листает страницы size ≤ 100,
 * пока не найдены все целевые id (или не кончился справочник).
 * Стоп-условие — по количеству фактически полученных записей (fetched < total),
 * а не page*size: total считается в записях, страница может быть неполной.
 */
async function collectNamesByPages<T>(
  fetchPage: (page: number) => Promise<Page<T>>,
  targetIds: string[],
  entryOf: (entity: T) => { id: string; name: string } | null,
): Promise<Map<string, string>> {
  const target = new Set(targetIds);
  if (target.size === 0) return new Map();
  const names = new Map<string, string>();
  let page = 0;
  let fetched = 0;
  let total = Number.POSITIVE_INFINITY;
  while (names.size < target.size && fetched < total) {
    const data = await fetchPage(page);
    if (data.content.length === 0) break;
    total = data.total;
    fetched += data.content.length;
    for (const entity of data.content) {
      const entry = entryOf(entity);
      if (entry && target.has(entry.id)) names.set(entry.id, entry.name);
    }
    page += 1;
  }
  return names;
}

/**
 * Map id → отображаемое имя пользователя для экспортной выборки.
 * Транспорт — {@code GET /api/users/lookup?ids=} чанками ≤100 (U-15; доступен всем ролям).
 */
export async function loadUserMapForExport(
  ids: Array<string | null | undefined>,
): Promise<Map<string, string>> {
  const unique = [...new Set(ids.filter((id): id is string => Boolean(id)))].sort();
  if (unique.length === 0) return new Map();
  const results = await Promise.all(
    chunkStrings(unique, IDS_CHUNK_SIZE).map((chunk) => usersApi.lookupByIds(chunk)),
  );
  return buildUserMap(results.flat());
}

/** Map id → название компании для экспортной выборки (постраничный сбор справочника). */
export async function loadCompanyMapForExport(
  ids: Array<string | null | undefined>,
): Promise<Map<string, string>> {
  const unique = [...new Set(ids.filter((id): id is string => Boolean(id)))];
  return collectNamesByPages(
    (page) => companiesApi.list({ page, size: DIRECTORY_PAGE_SIZE }),
    unique,
    (company) => ({ id: company.id, name: company.name }),
  );
}

/** Map id → «Имя Фамилия» контакта для экспортной выборки (постраничный сбор справочника). */
export async function loadContactMapForExport(
  ids: Array<string | null | undefined>,
): Promise<Map<string, string>> {
  const unique = [...new Set(ids.filter((id): id is string => Boolean(id)))];
  return collectNamesByPages(
    (page) => contactsApi.list({ page, size: DIRECTORY_PAGE_SIZE }),
    unique,
    (contact) => ({ id: contact.id, name: contactDisplayName(contact) }),
  );
}

/**
 * Map id → название сделки для экспортной выборки: поштучный GET /api/deals/{id}
 * чанками (batch-endpoint'а нет; 404/удалённая сделка → просто отсутствует в map).
 */
export async function loadDealMapForExport(
  ids: Array<string | null | undefined>,
): Promise<Map<string, string>> {
  const unique = [...new Set(ids.filter((id): id is string => Boolean(id)))].sort();
  const names = new Map<string, string>();
  for (const chunk of chunkStrings(unique, DEAL_FETCH_CHUNK_SIZE)) {
    const results = await Promise.all(
      chunk.map((id) =>
        dealsApi
          .get(id)
          .then((deal) => ({ id, title: deal.title } as const))
          .catch(() => null),
      ),
    );
    for (const result of results) {
      if (result) names.set(result.id, result.title);
    }
  }
  return names;
}

/**
 * Резолвит id в имя: из map — имя, иначе «—».
 * Отличие от resolve*Name из api/*: без коротких UUID-фолбэков (U-15: ни одного UUID).
 */
export function resolveNameOrDash(
  namesMap: Map<string, string> | undefined,
  id: string | null | undefined,
): string {
  if (!id) return EMPTY;
  return namesMap?.get(id) ?? EMPTY;
}
